<template>
	<section
		:class="[
			`section section--${size}`,
			{
				'section--splice': splice,
				'section--no-padding-top': noPaddingTop,
			},
		]"
	>
		<header v-if="header">
			<p v-html="header" class="section__header" />
			<span
				v-if="subHeader"
				:class="[
					'section__sub-header',
					{
						'section__sub-header--warning': warningSubHeader,
					},
				]"
				>{{ subHeader }}</span
			>
		</header>
		<template v-if="isTable">
			<section v-if="$slots.infoSection" class="section__info">
				<slot name="infoSection" />
			</section>
			<p
				v-if="tableTitle && tableTitle !== true"
				class="section__table-title"
			>
				{{ tableTitle }}
			</p>
			<section v-if="$slots.topBar" class="section__table-top-bar">
				<slot name="topBar" />
			</section>
			<body-scroll-table v-if="bodyScrollTable">
				<slot name="bodyScrollTable" />
			</body-scroll-table>
			<section
				v-if="!bodyScrollTable && dpTable"
				:style="tableWrapStyle"
				class="section__table-wrap"
			>
				<slot />
			</section>
			<div v-if="$slots.pagination" class="section__pagination">
				<slot name="pagination" />
			</div>
		</template>
		<template v-else>
			<p v-if="title" class="section__title">{{ title }}</p>
			<div
				v-show="!(diminishable && dim)"
				:style="{ ...gridStyle, height: contsHeight }"
				class="section__conts-wrap"
			>
				<slot />
			</div>
		</template>
		<button
			v-if="diminishable"
			type="button"
			:class="[
				'section__dim-button',
				{ 'section__dim-button--dim': dim },
			]"
			@click="dim = !dim"
		>
			<span class="section__dim-icon" />
		</button>
	</section>
</template>
<script>
	import BodyScrollTable from '@/components/BodyScrollTable';

	export default {
		name: 'ManagingSection',
		components: { BodyScrollTable },
		props: {
			size: {
				type: String,
				default: 'm',
			},
			header: {
				type: String,
				default: '',
			},
			isTable: {
				type: Boolean,
				default: false,
			},
			tableTitle: {
				type: [String, Boolean],
				default: '',
			},
			bodyScrollTable: {
				type: Boolean,
				default: false,
			},
			dpTable: {
				type: Boolean,
				default: true,
			},
			tableHeight: {
				type: Number,
				default: null,
			},
			tableMaxHeight: {
				type: Number,
				default: null,
			},
			title: {
				type: String,
				default: '',
			},
			columnCount: {
				type: Number,
				default: 1,
			},
			subHeader: {
				type: String,
				default: '',
			},
			contsHeight: {
				type: String,
				default: 'auto',
			},
			splice: {
				type: Boolean,
				default: false,
			},
			warningSubHeader: {
				type: Boolean,
				default: false,
			},
			diminishable: {
				type: Boolean,
				default: false,
			},
			noPaddingTop: {
				type: Boolean,
				default: false,
			},
		},
		data() {
			return {
				dim: true,
			};
		},
		computed: {
			getSlotLength() {
				return Math.ceil(
					this.$slots.default.filter(item =>
						item.componentOptions
							? item.componentOptions.tag === 'ManagingBox' ||
								item.componentOptions.tag === 'managing-box'
							: false
					).length / 2
				);
			},
			tableWrapStyle() {
				const styleObj = {};
				if (this.tableHeight) {
					styleObj.height = `${this.tableHeight}px`;
					styleObj.overflow = 'auto';
				} else if (this.tableMaxHeight) {
					styleObj.height = 'auto';
					styleObj.maxHeight = `${this.tableMaxHeight}px`;
					styleObj.overflow = 'auto';
				}
				return styleObj;
			},
			gridStyle() {
				const columnCount = this.columnCount;
				const style = {};
				if (columnCount === 1) {
					style.gridTemplateColumns = `repeat(${columnCount}, 1fr)`;
				} else {
					style.gridTemplate = `repeat(${this.getSlotLength}, auto) / repeat(${columnCount}, 1fr)`;
				}
				return style;
			},
		},
	};
</script>
<style lang="scss" scoped>
	.section {
		padding: 24px 0 0 0;

		&__dim-button {
			display: block;
			margin: 0 auto;
			padding: 10px;
			border: solid 1px #dfe6ee;
			border-top-width: 0;

			&--dim {
				border-top-width: 1px;

				.section__dim-icon {
					border-bottom: 0;
					border-top: solid 20px #657088;
					border-left: solid 30px white;
					border-right: solid 30px white;
				}
			}
		}

		&__dim-icon {
			display: block;
			width: 60px;
			height: 20px;
			border-bottom: solid 20px #657088;
			border-left: solid 30px white;
			border-right: solid 30px white;
		}

		&--splice {
			padding-top: 0 !important;

			.section__conts-wrap {
				border-top: 0;
			}

			.section__dim-button--dim {
				border-top-width: 0;
			}
		}

		& + .section {
			padding: 60px 0 0 0;

			&--no-padding-top {
				padding-top: 0;
			}
		}

		&--no-padding-top {
			padding-top: 0;
		}
	}
	.glowpick .section {
		padding: 0;
	}

	.section--s ::v-deep .box__header {
		display: flex;
		width: 143px;
		padding: 6px;
		align-items: center;
		justify-content: center;
	}

	.section--m ::v-deep .box__header {
		width: 132px;
	}

	.section--l ::v-deep .box__header {
		width: 172px;
	}

	.section__header {
		display: inline-block;
		line-height: 16px;
		color: #303643;
		font-size: 1rem;
		font-weight: bold;
		padding-left: 20px;
		margin-bottom: 12px;
	}

	.section__sub-header {
		margin-left: 12px;
		color: $font-blue;
	}

	.section__sub-header--warning {
		color: $red;
		font-weight: bold;
	}

	.section__info {
		display: flex;
		margin-bottom: 14px;
		justify-content: space-between;
		align-items: center;
	}

	.section__info__left,
	.section__info__right {
		display: flex;
		align-items: center;
		font-size: 0.875rem;
	}

	.section__info__items {
		display: block;
		margin-left: 16px;
		font-size: 0.875rem;

		&:first-child {
			margin-left: 0;
		}
	}

	.section__table-title {
		display: block;
		width: 100%;
		height: 56px;
		background: #495470;
		line-height: 56px;
		text-align: center;
		color: #fff;
		font-size: 1.375rem;
		font-weight: 700;
	}

	.section__table-top-bar {
		background: #dfe6ee;
		padding: 10px 12px;
		font-size: 0;
		text-align: justify;

		&:after {
			content: '';
			display: inline-block;
			width: 100%;
		}
	}

	.section__table-wrap {
		overflow-x: auto;
		border: 1px solid #dfe6ee;

		.table-main::v-deep {
			thead:first-child tr:first-child th {
				border-top: none;
			}

			tbody:last-child tr:last-child td {
				border-bottom: none;
			}

			th:first-child,
			td:first-child {
				border-left: none;
			}

			th:last-child,
			td:last-child {
				border-right: none;
			}

			tfoot:last-child tr:last-child td {
				border-bottom: none;
			}
		}
	}

	.section__pagination {
		padding-top: 40px;
	}

	.section__title {
		background: linear-gradient(to right, #dfe6ee, #f4f7fa, #f4f7fa);
		border: solid 1px #dfe6ee;
		border-bottom: none;
		line-height: 26px;
		text-align: center;
	}

	.section__conts-wrap {
		display: grid;
		width: 100%;
		border-top: solid 1px #dfe6ee;

		@media (max-width: 768px) {
			display: block;
		}
	}
</style>
